import {
  priceFromCost,
  productDeletionWarning,
  toDecimal,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync, useRememberedState } from "../../lib/hooks";
import { deleteRow, insertRow, updateRow } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";
import { CompanyDocsTool } from "./CompanyDocsTool";
import { CrewsTool } from "./CrewsTool";
import { EmailsTool } from "./EmailsTool";
import { FeaturesTool } from "./FeaturesTool";
import { IntakeLinksTool } from "./IntakeLinksTool";
import { NeighbourLettersTool } from "./NeighbourLettersTool";
import { IntegrationsTool, isQuickbooksReturn } from "./IntegrationsTool";
import { NotificationsTool } from "./NotificationsTool";
import { PersonalTextsTool } from "./PersonalTextsTool";
import { ColorLinksTool } from "./ColorLinksTool";
import { ProductVideosTool } from "./ProductVideosTool";
import { PricingTool } from "./PricingTool";
import { SupplierMailTool } from "./SupplierMailTool";
import { SuppliersTool } from "./SuppliersTool";
import { TemplatesTool } from "./TemplatesTool";
import { UpgradesTool } from "./UpgradesTool";
import { UsersTool } from "./UsersTool";

type Column = [field: string, label: string, kind?: "num"];

interface Section {
  name: string;
  table?: string;
  columns?: Column[];
  readOnly?: boolean;
  searchable?: boolean;
  custom?:
    | "suppliers"
    | "crews"
    | "users"
    | "notifications"
    | "templates"
    | "supplierPricing"
    | "emails"
    | "companyDocs"
    | "features"
    | "integrations"
    | "upgrades"
    | "productVideos"
    | "personalTexts"
    | "supplierMail"
    | "intakeLinks"
    | "colorLinks"
    | "neighbourLetters";
  /** Shown only to the people whose own phone syncs into Roof Board. */
  mineOnly?: boolean;
  /** Reference lists must never be renamed — see below. */
  noRename?: boolean;
  seed?: Record<string, unknown>;
}

/**
 * Tools is the settings surface (ADR 0018) and reference lists live in tables,
 * not in code (ADR 0019).
 *
 * Renaming is blocked on trades, lead sources and suppliers: documents
 * store those values as free text, so a rename silently orphans every row
 * carrying the old string. Add a new value and deactivate the old one instead
 * (resolved open item A2).
 */
const SECTIONS: Section[] = [
  { name: "Company Docs", custom: "companyDocs" },
  { name: "Crews", table: "crews", custom: "crews" },
  {
    name: "Checklists",
    table: "checklist_templates",
    readOnly: true,
    columns: [
      ["name", "Name"],
      ["phase", "Phase"],
      ["bid_type", "Bid Type"],
    ],
  },
  { name: "Roof Templates", custom: "templates" },
  { name: "Supplier Pricing", custom: "supplierPricing" },
  // Supplier emails to orders@ the app could not place (ADR 0311). Beside
  // Supplier Pricing, because that is where somebody looking for "what did
  // ABC send us" starts.
  { name: "Supplier Mail", custom: "supplierMail" },
  { name: "Emails", custom: "emails" },
  { name: "SMS" },
  // Jonathon only, and absent from the menu for everybody else — see ADR 0090.
  { name: "Personal Texts", custom: "personalTexts", mineOnly: true },
  { name: "Users", custom: "users" },
  { name: "Notifications", custom: "notifications" },
  { name: "Customer Links", custom: "intakeLinks" },
  // The colour options a rep sends on their own (Jonathon, 2026-09-30).
  { name: "Color Links", custom: "colorLinks" },
  {
    name: "Employees",
    table: "profiles",
    columns: [
      ["full_name", "Name"],
      ["email", "Email"],
      ["sort_order", "Tab Order", "num"],
    ],
  },
  { name: "Estimates" },
  { name: "Integrations", custom: "integrations" },
  // Whole sections that are switched off. One entry so far — the partner
  // network, built and never used (ADR 0144). Its own section rather than a
  // checkbox tucked into Integrations: "referral partners are off" is not a
  // fact about an integration, and somebody looking for it would never think
  // to open Mercury.
  { name: "Features", custom: "features" },
  // The two Roof Strategist letters that send themselves (ADR 0149). Its own
  // section for the same reason Features is: somebody looking for "which
  // letter do the neighbours get" would not open Emails or Integrations.
  { name: "Neighbour Letters", custom: "neighbourLetters" },
  {
    name: "Lost Reasons",
    table: "lost_reasons",
    noRename: true,
    columns: [
      ["name", "Name"],
      ["sort_order", "Order", "num"],
    ],
  },
  {
    name: "Lead Sources",
    table: "lead_sources",
    noRename: true,
    columns: [
      ["name", "Name"],
      ["sort_order", "Order", "num"],
    ],
  },
  // Regions was here. Jonathon, 2026-08-13: "let's just remove region from
  // roof-board." It was a required field on Verify that nobody used for
  // anything downstream — no pricing, no routing, no report read it. The
  // `regions` table and `jobs.region_id` are still in the database holding what
  // was already entered; nothing in the app writes or shows them.
  { name: "Work Orders" },
  { name: "Custom Reports" },
  {
    name: "Products",
    table: "products",
    searchable: true,
    columns: [
      ["name", "Name"],
      // What else the crews call it — "shingle nails, roofing nails". The
      // product pickers search these words like the name (ADR 0212), so a
      // supplier's label nobody uses can be found by the word everybody does.
      ["search_terms", "Also known as"],
      ["category", "Category"],
      ["unit", "Unit"],
      ["kind", "Type"],
      ["customer_wording", "Customer Wording"],
      // The scope that wording gets on an estimate line — what comes off, what
      // goes on. Gutters and guards carry one; almost nothing else does.
      ["customer_scope", "Customer Scope"],
      ["default_cost", "Cost", "num"],
      ["default_price", "Price", "num"],
    ],
    seed: {
      category: "Other",
      unit: "EA",
      kind: "material",
    },
  },
  { name: "Suppliers", table: "suppliers", custom: "suppliers" },
  // Its own short list rather than a filter over the 437 products: an upgrade
  // is a product with `proposal_upgrade_order` on it, and nobody was ever going
  // to find the four of them behind a search box.
  { name: "Proposal Upgrades", custom: "upgrades" },
  /*
   * The protective wording the AI attaches to a drafted scope (ADR 0100).
   *
   * Editable rather than written into the prompt because Jonathon said, in the
   * same breath as asking for it, "i'm not sold on this verbiage". These are
   * the sentences a customer reads on a document they sign; rewording one has
   * to be a row edit, not a code change and an Edge Function deploy.
   *
   * Renaming *is* allowed here, unlike the lists below it — an estimate line
   * stores the clause's id, not its name, so the name is a label and nothing
   * carries a copy of it.
   */
  /*
   * The paragraphs a customer reads before approving a change order.
   *
   * Here for the same reason the scope disclaimers are: they are sentences on
   * something a customer signs, and Jonathon rewords that kind of thing. A
   * document froze its own copy at send, so editing a row changes the next one
   * and never one already out. Renaming is allowed — the snapshot stores the
   * id, and the name is only a label in this table.
   */
  {
    name: "Change Order Terms",
    table: "change_order_terms",
    columns: [
      ["name", "Name"],
      ["body", "What the Customer Reads"],
      ["sort_order", "Order", "num"],
    ],
  },
  {
    name: "Scope Disclaimers",
    table: "scope_qualifiers",
    columns: [
      ["name", "Name"],
      ["body", "What the Customer Reads"],
      ["when_to_use", "When the AI Adds It"],
      ["sort_order", "Order", "num"],
    ],
  },
  // Its own screen for the same reason the upgrades have one: a walkthrough is
  // a product with a video on it, and nobody would find the handful of them
  // behind a search box over 600 rows. See ADR 0105.
  { name: "Product Videos", custom: "productVideos" },
  {
    name: "Trades",
    table: "trades",
    noRename: true,
    columns: [
      ["name", "Name"],
      ["sort_order", "Order", "num"],
    ],
  },
];

export function Tools() {
  // `/tools/quickbooks` is where Intuit returns from the QuickBooks consent
  // screen, carrying a single-use code that IntegrationsTool exchanges. It only
  // gets the chance if this opens on that section (ADR 0038) — which is why the
  // return beats the remembered section rather than the other way round.
  //
  // Otherwise the section survives a refresh. Editing a price list or a roof
  // template is the longest sit-down in the app and the one most likely to be
  // reloaded halfway through; landing back on Products each time is what
  // Jonathon asked to stop.
  const [selected, setSelected] = useRememberedState(
    "tools",
    "Products",
    isQuickbooksReturn(window.location.pathname) ? "Integrations" : null,
  );
  const { profile } = useSession();
  // A section nobody else has. Filtered out of the list rather than disabled in
  // it: a greyed-out "Personal Texts" would tell three reps that somebody's
  // phone is wired into Roof Board, which is not their business either.
  const sections = SECTIONS.filter(
    (s) => !s.mineOnly || profile?.syncs_personal_texts === true,
  );
  const section = sections.find((s) => s.name === selected) ?? sections[0]!;

  return (
    <>
      <div className="listhead">
        <h2>{t("Tools")}</h2>
      </div>
      <div className="tools">
        <div className="toolmenu">
          {sections.map((s) => (
            <div
              key={s.name}
              className={`toolitem${s.name === selected ? " active" : ""}${
                s.table || s.custom ? "" : " soon"
              }`}
              onClick={() => setSelected(s.name)}
            >
              {t(s.name)}
              {!s.table && !s.custom && (
                <span className="car">{t("soon")}</span>
              )}
            </div>
          ))}
        </div>
        <div className="block">
          <h2>{t(section.name)}</h2>
          {section.custom === "intakeLinks" ? (
            <IntakeLinksTool />
          ) : section.custom === "colorLinks" ? (
            <ColorLinksTool />
          ) : section.custom === "companyDocs" ? (
            <CompanyDocsTool />
          ) : section.custom === "suppliers" ? (
            <SuppliersTool />
          ) : section.custom === "crews" ? (
            <CrewsTool />
          ) : section.custom === "users" ? (
            <UsersTool />
          ) : section.custom === "notifications" ? (
            <NotificationsTool />
          ) : section.custom === "templates" ? (
            <TemplatesTool />
          ) : section.custom === "supplierPricing" ? (
            <PricingTool />
          ) : section.custom === "productVideos" ? (
            <ProductVideosTool />
          ) : section.custom === "upgrades" ? (
            <UpgradesTool />
          ) : section.custom === "emails" ? (
            <EmailsTool />
          ) : section.custom === "features" ? (
            <FeaturesTool />
          ) : section.custom === "neighbourLetters" ? (
            <NeighbourLettersTool />
          ) : section.custom === "integrations" ? (
            <IntegrationsTool />
          ) : section.custom === "personalTexts" ? (
            <PersonalTextsTool />
          ) : section.custom === "supplierMail" ? (
            <SupplierMailTool />
          ) : section.table ? (
            <TableEditor key={section.name} section={section} />
          ) : (
            <div className="shelf">
              <b>{t(section.name)}</b>
              {t("Not built yet.")}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Ask before deleting, and say plainly when the answer is no.
 *
 * A product any roof template orders cannot be deleted — the foreign key is
 * ON DELETE RESTRICT (migration 20260802140000), so the database refuses it.
 * Without this the person gets Postgres's own message, which names a constraint
 * and not a single thing they can do about it. This names the templates in the
 * way and points at deactivating, which is what rule 8 wanted anyway.
 *
 * The check is not the guardrail — the foreign key is. This is the explanation.
 */
async function confirmDelete(
  section: Section,
  row: Record<string, unknown>,
): Promise<boolean> {
  if (section.table !== "products") {
    return window.confirm(t("Delete this row?"));
  }

  const templates = unwrap<{ template: { name: string } | null }[]>(
    await supabase
      .from("roof_template_items")
      .select("template:roof_templates(name)")
      .eq("product_id", String(row.id)),
  );

  const blocked = productDeletionWarning(
    String(row.name ?? "This product"),
    templates.map((t) => t.template?.name ?? "(unnamed template)"),
  );

  if (blocked) {
    window.alert(blocked);
    return false;
  }
  return window.confirm(t("Delete this row?"));
}

/** "Products" → "product", for a prompt that reads like a sentence. */
function singular(sectionName: string): string {
  return sectionName.toLowerCase().replace(/s$/, "");
}

/**
 * Why the add did not happen, in the words of the thing being added.
 *
 * Add used to seed a fixed name — "New product" — against a table with a unique
 * index on `name`. So the first add worked, every one after it was refused with
 * `duplicate key value violates unique constraint "products_name_unique"`, and
 * that is what the screen showed: a constraint name, on a button that now just
 * looked broken. The name is asked for instead, and a clash is caught here.
 *
 * The check is deliberately case-insensitive where the index is not. Two
 * products called "GAF Timberline" and "gaf timberline" satisfy Postgres and
 * nobody else; the catalogue is picked from by eye.
 */
function duplicateNameMessage(section: Section, name: string): string {
  // "a product", "a lead source": the article travels with the noun, so
  // the Spanish can give each its own gender.
  const thing = t(`a ${singular(section.name)}`);
  return t(
    'There is already {thing} called "{name}". Search for it above to edit it, or add this one under a different name.',
    { thing, name },
  );
}

function TableEditor({ section }: { section: Section }) {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(60);
  const [error, setError] = useState<string | null>(null);

  const query = useAsync(async () => {
    let request = supabase.from(section.table!).select("*");
    request =
      section.table === "products" || section.table === "checklist_templates"
        ? request.order("name")
        : request.order("sort_order").order("name");
    return unwrap<Record<string, unknown>[]>(await request);
  }, [section.table]);

  const all = query.data ?? [];
  const filtered =
    section.searchable && search
      ? all.filter((row) =>
          `${row.name ?? ""} ${row.category ?? ""}`
            .toLowerCase()
            .includes(search.toLowerCase()),
        )
      : all;
  const shown = filtered.slice(0, limit);
  const columns = section.columns ?? [];

  const guard = async (action: () => Promise<void>) => {
    try {
      await action();
      setError(null);
      await query.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <ErrorNote error={error} />
      <div className="toolbar2">
        {section.searchable && (
          <input
            placeholder={t("Search…")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setLimit(60);
            }}
          />
        )}
        <span className="count">
          {tn(filtered.length, "1 record", "{n} records")}
          {filtered.length > shown.length
            ? t(" · showing {n}", { n: shown.length })
            : ""}
        </span>
        {!section.readOnly && (
          <button
            className="addrow"
            style={{ marginTop: 0 }}
            onClick={() =>
              void guard(async () => {
                const name = window
                  .prompt(
                    t("Name {thing}", {
                      thing: t(`the new ${singular(section.name)}`),
                    }),
                  )
                  ?.trim();
                if (!name) return;

                if (
                  all.some(
                    (row) =>
                      String(row.name ?? "")
                        .trim()
                        .toLowerCase() === name.toLowerCase(),
                  )
                ) {
                  throw new Error(duplicateNameMessage(section, name));
                }

                await insertRow(section.table!, {
                  ...(section.seed ?? {}),
                  name,
                });
                // 635 products sorted by name: without this the new row is
                // somewhere on page eleven and the button looks broken.
                if (section.searchable) {
                  setSearch(name);
                  setLimit(60);
                }
              })
            }
          >
            {t("+ Add")}
          </button>
        )}
      </div>

      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              {columns.map(([field, label, kind]) => (
                <th key={field} className={kind === "num" ? "num" : undefined}>
                  {t(label)}
                </th>
              ))}
              {!section.readOnly && <th />}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="tnone">
                  {t("Nothing here.")}
                </td>
              </tr>
            ) : (
              shown.map((row) => (
                <tr key={String(row.id)}>
                  {columns.map(([field, , kind]) => {
                    const lockName = section.noRename && field === "name";
                    return (
                      <td
                        key={field}
                        className={kind === "num" ? "num" : undefined}
                      >
                        {section.readOnly || lockName ? (
                          <span className={lockName ? "lockedval" : undefined}>
                            {String(row[field] ?? "—")}
                          </span>
                        ) : (
                          <CellInput
                            value={row[field]}
                            numeric={kind === "num"}
                            onSave={(value) =>
                              void guard(async () => {
                                const patch: Record<string, unknown> = {
                                  [field]: value,
                                };
                                // Keep the 40% gross profit rule intact.
                                if (
                                  section.table === "products" &&
                                  field === "default_cost"
                                ) {
                                  patch.default_price = value
                                    ? toDecimal(priceFromCost(value))
                                    : null;
                                }
                                await updateRow(
                                  section.table!,
                                  String(row.id),
                                  patch,
                                );
                              })
                            }
                          />
                        )}
                      </td>
                    );
                  })}
                  {!section.readOnly && (
                    <td>
                      <button
                        className="xbtn"
                        onClick={() =>
                          void guard(async () => {
                            if (!(await confirmDelete(section, row))) return;
                            await deleteRow(section.table!, String(row.id));
                          })
                        }
                      >
                        ✕
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > shown.length && (
        <button
          className="addrow ghost"
          onClick={() => setLimit((l) => l + 60)}
        >
          {t("Show more")}
        </button>
      )}

      {section.table === "products" && (
        <div className="na-hint" style={{ marginTop: 10 }}>
          {t(
            "Changing Cost recalculates Price at cost ÷ 0.6 (40% gross profit).",
          )}
        </div>
      )}
      {section.noRename && (
        <div className="na-hint" style={{ marginTop: 10 }}>
          {t(
            "Names cannot be changed — jobs and documents store this value as text, so renaming would orphan them. Add a new one and deactivate the old.",
          )}
        </div>
      )}
    </>
  );
}

function CellInput({
  value,
  numeric,
  onSave,
}: {
  value: unknown;
  numeric?: boolean;
  onSave: (value: string | number | null) => void;
}) {
  const initial = value == null ? "" : String(value);
  const [local, setLocal] = useState(initial);
  const [dirty, setDirty] = useState(false);
  if (!dirty && initial !== local) setLocal(initial);

  return (
    <input
      className={numeric ? "num" : undefined}
      value={local}
      onChange={(e) => {
        setLocal(e.target.value);
        setDirty(true);
      }}
      onBlur={() => {
        if (!dirty) return;
        setDirty(false);
        if (numeric) {
          const parsed = Number.parseFloat(local);
          onSave(Number.isFinite(parsed) ? parsed : null);
        } else {
          onSave(local === "" ? null : local);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
